import { CloseButton } from '@headlessui/react';
import { PaperAirplaneIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Form, Input, Select, SelectItem, Textarea } from '@heroui/react';
import { ErrorMessage } from '@hookform/error-message';
import { useEffect } from 'react';
import { Controller, type SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useAuth } from '@/app/hooks/auth-provider.tsx';
import { useSecurityContext } from '@/app/hooks/security-provider.tsx';
import toast from '@/app/toast.ts';
import { Dialog, DialogBody, DialogDescription, DialogTitle } from '@/primitives/dialog.tsx';
import {
  Description,
  Field,
  FieldErrorMessage,
  FieldGroup,
  Fieldset,
  Label,
} from '@/primitives/fieldset.tsx';
import { usePostInviteRepresentativeMutation } from '@/slices/representative-api-slice.ts';
import { emailPattern } from '../../types/constants.ts';
import { UserTypeEnum } from '../../types/user-type.ts';
import { usePageLoading } from '../page-loading.tsx';

export interface InviteRepModel {
  message?: string;
  reps: InviteRepRecipient[];

  // This is the account that the rep will be associated with in CRM (optional for admins, required for staff members).
  referringAccountKey?: string;
}

export interface InviteRepRecipient {
  emailAddress: string;
  name: string;
}

const InviteRepDialog = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) => {
  const { user } = useAuth();
  const { currentAccount, visibleAccounts: accounts } = useSecurityContext();

  const { register, control, reset, formState, getFieldState, handleSubmit, setValue, getValues } =
    useForm<InviteRepModel>({
      defaultValues: {
        message: '',
        referringAccountKey: '',
        reps: [{ emailAddress: '', name: '' }],
      },
    });

  const [post, { isError, isSuccess, isLoading }] = usePostInviteRepresentativeMutation();
  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'reps',
  });

  const userIsAdmin = user?.userType === UserTypeEnum.Admin;
  const userHasSingleVisibleAccount = (accounts?.length ?? 0) === 1;

  // Initialize referring account for non-admins when the dialog opens if a current account is already selected.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!userIsAdmin && currentAccount?.key && !getValues('referringAccountKey')) {
      setValue('referringAccountKey', currentAccount.key, { shouldValidate: true });
    }
  }, [isOpen, userIsAdmin, currentAccount, getValues, setValue]);

  useEffect(() => {
    if (isSuccess) {
      toast.success('Invitation has been successfully sent!');
      onClose(false);
    }

    if (isError) {
      toast.error('There was an error sending the invitation.');
    }
  }, [isError, isSuccess]);

  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    isLoading ? showPageLoading('rep-invite-post') : hidePageLoading('rep-invite-post');
  }, [isLoading]);

  const onClose = (open: boolean) => {
    reset();
    setIsOpen(open);
  };

  const onSubmit: SubmitHandler<InviteRepModel> = (value: InviteRepModel) => {
    const referringAccountKey =
      userHasSingleVisibleAccount && !userIsAdmin
        ? accounts?.[0]?.key
        : value.referringAccountKey?.trim() || undefined;

    post({
      ...value,
      referringAccountKey,
    });
  };

  return (
    <Dialog size='2xl' onClose={onClose} open={isOpen}>
      <Form className='flex flex-col items-stretch' onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-row items-end justify-between mb-4'>
          <DialogTitle>Invite Representative</DialogTitle>
          <CloseButton
            title='Cancel'
            as={Button}
            size='sm'
            isIconOnly
            variant='flat'
            color='default'
          >
            <XMarkIcon className='size-6' />
          </CloseButton>
        </div>
        <DialogDescription>
          Submitting the form below will send an email to invite a drug representative to sign up
          for MeetingMD.
        </DialogDescription>
        <DialogBody>
          <Fieldset>
            <div className='flex flex-col gap-2 w-full'>
              {fields.map((field, index) => (
                <div key={field.id} className='flex flex-row items-center gap-2 w-full'>
                  <Field className='basis-6/12'>
                    <Input
                      label='Email address'
                      type='email'
                      variant='faded'
                      color='default'
                      isInvalid={getFieldState(`reps.${index}.emailAddress`).invalid}
                      {...register(`reps.${index}.emailAddress`, {
                        required: 'This field is required',
                        pattern: {
                          value: emailPattern,
                          message: 'Please enter a valid email address',
                        },
                      })}
                    />
                    <ErrorMessage
                      as={<FieldErrorMessage />}
                      name={`reps.${index}.emailAddress`}
                      errors={formState.errors}
                    />
                  </Field>
                  <Field className='basis-4/12'>
                    <Input
                      label='Name'
                      type='text'
                      variant='faded'
                      color='default'
                      placeholder={'(Optional)'}
                      {...register(`reps.${index}.name`)}
                    />
                  </Field>
                  <div className='basis-2/12 flex justify-end items-center'>
                    {index > 0 && (
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        color='default'
                        onPress={() => remove(index)}
                        title='Remove recipient'
                      >
                        <XMarkIcon className='size-4' />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <Button
              size='sm'
              variant='flat'
              color='primary'
              onPress={() => append({ emailAddress: '', name: '' })}
              title='Add recipient'
              className='mt-2'
            >
              <PlusIcon className='size-5' />
              Add Another Recipient
            </Button>

            <FieldGroup>
              <Field>
                <Label>Message</Label>
                <Textarea cols={3} {...register('message')} placeholder={'(Optional)'} />
              </Field>

              {!userHasSingleVisibleAccount && (
                <Field>
                  <Label required={!userIsAdmin}>Account</Label>
                  <Controller
                    control={control}
                    name='referringAccountKey'
                    rules={{
                      required: userIsAdmin
                        ? false
                        : 'Please select an account to send the invitation from.',
                    }}
                    render={({ field, fieldState }) => (
                      <Select
                        name={field.name}
                        labelPlacement='outside'
                        placeholder='--Account--'
                        variant='faded'
                        color='default'
                        items={accounts ?? []}
                        isDisabled={!accounts?.length}
                        isInvalid={fieldState.invalid}
                        errorMessage={fieldState.error?.message}
                        selectedKeys={field.value ? new Set([field.value]) : new Set([])}
                        onBlur={field.onBlur}
                        onSelectionChange={keys => {
                          const selected = Array.from(keys)[0]?.toString() ?? '';
                          field.onChange(selected);
                        }}
                      >
                        {account => (
                          <SelectItem key={account.key}>{account.accountName}</SelectItem>
                        )}
                      </Select>
                    )}
                  />
                  {userIsAdmin && (
                    <Description>
                      Admin info: This is the referring account for CRM. It is optional for admins.
                    </Description>
                  )}
                </Field>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <div className='flex justify-end pt-10'>
          <div>
            <Button color='primary' type='submit' isLoading={isLoading}>
              <PaperAirplaneIcon className='size-5' />
              Send
            </Button>
          </div>
        </div>
      </Form>
    </Dialog>
  );
};

export default InviteRepDialog;
